/**
 * TypeScript interfaces for Integration components
 * Defines types for app connections, profiles, and tool configurations
 */

/**
 * Integration connection details
 * Contains metadata about an established connection
 */
export interface IntegrationConnectionDetails {
  resourceId: string;
  connectedAt: string;
  status: string;
  id: string;
  name?: string; // Connection instance name
  email?: string; // Email for OAuth connections
  displayName?: string; // Optional display name for the connection (e.g., email address)
  config?: any;
  createdAt?: string;
}

/**
 * Integration connection configuration
 * Defines available connection types for an integration
 */
export interface IntegrationConnection {
  id: string;
  name: string;
  type: string;
  isConnected: boolean;
  connectionDetails?: IntegrationConnectionDetails[];
}

/**
 * Connected integration instance (for multiple connections of same type)
 * Represents a single active connection instance
 */
export interface ConnectedIntegrationInstance {
  id: string;
  displayName: string;
  resourceId: string;
  createdAt: Date;
  config: any;
  connectionId: string;
}

/**
 * Integration response from API
 * Full integration object with metadata and connections
 */
export interface Integration {
  id: string;
  name: string;
  description: string;
  logo?: string;
  logoUrl?: string;
  isPublished: boolean;
  availableForAgent: boolean;
  isLlmModel: boolean;
  isPremium?: boolean;
  category?: string;
  models?: string[];
  connections: IntegrationConnection[];
  actions?: IntegrationAction[];
}

/**
 * Integration action definition
 * Describes a single action/operation an integration can perform
 */
export interface IntegrationAction {
  id: string;
  name: string;
  description: string;
  available?: boolean;
}

/**
 * Selected integration with chosen actions
 * Represents an integration added to an agent
 */
export interface SelectedIntegration {
  appId: string;
  integrationId?: string;
  name: string;
  type: string;
  tools: string[];
  logo?: string;
  description: string;
  connectionId: string;
  qualifiedName?: string;
  config?: unknown;
  selectedConnectionInstance?: string;
  selectedActions?: string[];
}

/**
 * API response for integrations list
 * Standard response format from integrations API endpoint
 */
export interface IntegrationsApiResponse {
  success: boolean;
  data: Integration[];
  message: string;
}

/**
 * Integration card props
 * Props for displaying integration in card format
 */
export interface IntegrationCardProps {
  integration: Integration;
  selectedIntegration?: SelectedIntegration;
  onConnect: (integrationId: string, connectionId: string) => void;
  onDisconnect: (integrationId: string) => void;
  onActionsChange: (integrationId: string, actions: string[]) => void;
  onConnectionInstanceChange: (
    integrationId: string,
    connectionInstanceId: string
  ) => void;
  className?: string;
}

/**
 * Action selector props
 * Props for the action selection dropdown component
 */
export interface ActionSelectorProps {
  integrationId: string;
  availableActions: IntegrationAction[];
  selectedActions: string[];
  onActionsChange: (actions: string[]) => void;
  disabled?: boolean;
  className?: string;
}

export interface ComposioApp {
  authSchemes: string[];
  categories: string[];
  description: string;
  logo: string;
  name: string;
  slug: string;
}

export interface ComposioProfile {
  profileId: string;
  profileName: string;
  displayName: string;
  toolkitSlug: string;
  toolkitName: string;
  mcpUrl: string;
  redirectUrl?: string;
  connectedAccountId?: string;
  isConnected: boolean;
  isDefault: boolean;
  createdAt: string;
}

/**
 * Represents a field in authentication configuration
 */
export interface AuthConfigField {
  // Internal name of the field
  name: string;
  // Display name shown to users
  displayName: string;
  // Data type of the field value
  type: string;
  // Description of what the field is for
  description?: string;
  // Whether the field is required
  required?: boolean;
  // Default value if not provided
  default?: string;
  // Legacy template name for backward compatibility
  legacyTemplateName?: string;
}

export interface ComposioTool {
  slug: string;
  name: string;
  description: string;
  version: string;
  input_parameters: {
    properties: Record<string, any>;
    required?: string[];
  };
  output_parameters: {
    properties: Record<string, any>;
  };
  scopes?: string[];
  tags?: string[];
  no_auth: boolean;
}

export interface IntegrationApp extends ComposioApp {
  id?: string;
  type: "composio" | "internal" | "mcp";
  logoUrl?: string;
  category?: string;
  isPremium?: boolean;
  connections?: any[];
  actions?: any[];
}

/**
 * Extended integration app interface with optional properties
 * Includes additional fields that may be present on integration objects
 */
export interface ExtendedIntegrationApp extends IntegrationApp {
  /**
   * Alternative logo URL field
   * Used as fallback when main logo is not available
   */
  logoUrl?: string;
  
  /**
   * Premium status indicator
   * Shows if integration requires premium subscription
   */
  isPremium?: boolean;
  
  /**
   * Array of available actions for the integration
   * Contains the list of operations the integration can perform
   */
  actions?: Array<{ length: number }>;
  
  /**
   * Array of existing connections for the integration
   * Shows current connected instances of this integration
   */
  connections?: Array<{
    id: string;
    name: string;
    isConnected: boolean;
  }>;
  
  /**
   * Category classification for the integration
   * Used for filtering and organization purposes
   */
  category?: string;
}

/**
 * Handler function for app connection completion
 * Contains all necessary data for successful connection
 */
export interface CompleteHandler {
  /**
   * The integrated app configuration
   * Contains app metadata and connection details
   */
  app: IntegrationApp;
  
  /**
   * Profile information for the connection
   * Contains unique identifier for the profile
   */
  profile: { id: string };
  
  /**
   * Array of selected tools/functions
   * Contains tool identifiers that will be enabled
   */
  tools: string[];
  
  /**
   * Optional configuration parameters
   * Contains key-value pairs for connection setup
   */
  config?: Record<string, unknown>;
}

/**
 * Reusable trigger record interface for trigger data handling
 * Contains trigger-specific data used across components and API calls
 */
export interface TriggerRecord {
  /**
   * The integrated app configuration
   * Contains app metadata and connection details
   */
  app: IntegrationApp;

  /**
   * Profile information for the connection
   * Contains unique identifier for the profile
   */
  profile: { id: string };

  /**
   * Trigger information with details
   * Contains trigger slug, name, and optional description
   */
  trigger: {
    /**
     * Unique slug identifier for the trigger
     * Used to identify the trigger in the system
     */
    slug: string;

    /**
     * Human-readable name of the trigger
     * Displayed in the UI for user reference
     */
    name: string;

    /**
     * Optional description of the trigger
     * Provides additional context about trigger behavior
     */
    description?: string;
  };

  /**
   * Optional configuration parameters
   * Contains key-value pairs for trigger setup including custom field values
   */
  config?: Record<string, unknown>;
}

/**
 * App connection workflow steps enumeration
 * Defines the sequence of connection process stages
 */
export enum Step {
  /**
   * Initial step for selecting existing profiles
   * Allows users to choose from connected profiles
   */
  ProfileSelect = "profile-select",
  
  /**
   * Step for creating new connection profiles
   * Handles profile creation and authentication setup
   */
  ProfileCreate = "profile-create",
  
  /**
   * Authentication process step
   * Manages OAuth and credential verification
   */
  Connecting = "connecting",
  
  /**
   * Tool selection and configuration step
   * Allows users to choose which tools to enable
   */
  ToolsSelection = "tools-selection",
  
  /**
   * Final completion step
   * Confirms successful connection establishment
   */
  Success = "success",
}

/**
 * Configuration for each workflow step
 * Defines visual and behavioral properties for steps
 */
export interface StepConfig {
  /**
   * Unique identifier for the step
   * Maps to Step enum values
   */
  id: Step;
  
  /**
   * Display title for the step
   * Shown in step indicator and headers
   */
  title: string;
  
  /**
   * Optional description text
   * Provides additional context for the step
   */
  description?: string;
  
  /**
   * React icon component for visual representation
   * Displayed in step indicators
   */
  icon: React.ReactNode;
  
  /**
   * Whether to show this step in progress indicators
   * Controls visibility in step navigation
   */
  showInProgress?: boolean;
}

/**
 * Toolkit connection details response from API
 * Contains authentication and configuration requirements
 */
export interface ToolkitDetails {
  /**
   * Main toolkit configuration object
   * Contains connection and authentication details
   */
  toolkit: {
    /**
     * Logo URL for the toolkit
     * Used for displaying app icons in connection dialogs
     */
    logo?: string;

    /**
     * Authentication configuration details
     * Contains OAuth2 and API key authentication configurations
     */
    authConfigDetails?: Array<{
      mode: string;
      name: string;
      fields: {
        connectedAccountInitiation?: {
          required?: AuthConfigField[];
          optional?: AuthConfigField[];
        };
        authConfigCreation?: {
          required?: AuthConfigField[];
          optional?: AuthConfigField[];
        };
      };
    }>;

    /**
     * Fields required for connection initialization
     * Contains authentication and setup parameters
     */
    connectedAccountInitiationFields?: {
      /**
       * Required fields that must be provided
       * Cannot be empty for successful connection
       */
      required?: AuthConfigField[];

      /**
       * Optional fields for additional configuration
       * Can be left empty without affecting connection
       */
      optional?: AuthConfigField[];
    };
  };

  /**
   * Available connections for internal apps
   * Contains connection types and their configuration fields
   */
  connections?: Array<{
    id: string;
    name: string;
    description?: string;
    type: string;
    fields: AuthConfigField[];
  }>;
}

/**
 * Tools list API response structure
 * Contains array of available tools for integration
 */
export interface ToolsResponse extends Array<ComposioTool> {
  /**
   * Length property for array-like behavior
   * Indicates number of available tools
   */
  length: number;
}
